import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';

import type {
  BulkSharedCapacityEligibilityResult,
  BulkSharedCapacityRequest,
  BulkSharedCapacityResult,
} from './types';

const unwrapBulkSharedCapacity = <T>(
  response: unknown,
  fallback: string,
): T => {
  const body = response as V4ApiResponse<T>;
  if (!body.isSuccess || body.value == null) {
    const message = body.errors
      .map((error) => error.message)
      .filter((errorMessage) => errorMessage.length > 0)
      .join('\n');
    throw new Error(message.length > 0 ? message : fallback);
  }

  return body.value;
};

export async function checkBulkSharedCapacityEligibility(loadIds: number[]) {
  try {
    const response = await apiClient.post(
      'load/sharecapacity/bulkAddEligibleLoadId',
      loadIds,
    );
    return unwrapBulkSharedCapacity<BulkSharedCapacityEligibilityResult>(
      response,
      'Shared capacity eligibility check failed',
    );
  } catch (error: unknown) {
    const wrapped = createError(
      error,
      'Failed to check shared capacity eligibility',
    );
    logError('checkBulkSharedCapacityEligibility', wrapped);
    throw wrapped;
  }
}

export async function bulkAddSharedCapacity(
  items: BulkSharedCapacityRequest[],
) {
  try {
    const response = await apiClient.post(
      'load/sharecapacity/bulkAdd',
      items,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.loadboard.massShareCapacity,
      ]),
    );
    return unwrapBulkSharedCapacity<BulkSharedCapacityResult>(
      response,
      'Failed to add shared capacity',
    );
  } catch (error: unknown) {
    const wrapped = createError(error, 'Failed to add shared capacity');
    logError('bulkAddSharedCapacity', wrapped);
    throw wrapped;
  }
}
